import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

export function ExpandableList<T>({
  items,
  expanded,
  maxItems = 3,
  setExpanded,
  renderItem,
}: {
  items: T[];
  expanded: boolean;
  maxItems?: number;
  setExpanded: (expanded: boolean) => void;
  renderItem: (item: T) => string;
}) {
  const { t } = useTranslation(['common']);
  if (!items || items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }

  const prepareDisplayText = () => {
    const displayItems = expanded ? items : items.slice(0, maxItems);
    const displayText = displayItems.map(renderItem).join(', ');
    const suffix = !expanded && items.length > maxItems ? '...' : '';

    return displayText + suffix;
  };

  const displayText = prepareDisplayText();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
      }}>
      <Box
        sx={{
          ...(expanded && {
            maxHeight: '100px',
            overflowY: 'auto',
          }),
          width: '100%',
        }}>
        <Typography variant="body2" color="text.secondary">
          {displayText}
        </Typography>
      </Box>

      {items.length > maxItems && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Typography
            component="span"
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => setExpanded(!expanded)}>
            {expanded ? t('common:collapse') : t('common:expand')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
